/**
 * @file DateTimePicker 日期时间
 * @description 日期 + 时分；值格式 `YYYY-MM-DD HH:mm`。
 *
 * @whenToUse 预约、截止时间等需精确到分钟。
 * @whenNotToUse 仅日期（DatePicker）；仅时分（TimePicker）。
 */

import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { CalendarClock } from 'lucide-react'
import { cn } from '@/lib/cn.ts'
import { formatDate, parseDate } from '@/lib/date.ts'
import {
  computeDropdownBox,
  floatingPanelStyle,
  type FloatingBox,
} from '@/lib/floating.ts'
import { Button } from '@/components/ui/button.tsx'
import { Calendar } from '@/components/ui/calendar.tsx'
import { TimePicker } from '@/components/ui/time-picker.tsx'

export type DateTimePickerProps = {
  /** `YYYY-MM-DD HH:mm` */
  value?: string
  onChange?: (value: string) => void
  placeholder?: string
  disabled?: boolean
  invalid?: boolean
  className?: string
  id?: string
  minuteStep?: number
}

function splitValue(value?: string): { date?: string; time?: string } {
  if (!value) return {}
  const [date, time] = value.split(' ')
  return { date, time }
}

export function DateTimePicker({
  value,
  onChange,
  placeholder = '选择日期时间',
  disabled = false,
  invalid = false,
  className,
  id,
  minuteStep = 5,
}: DateTimePickerProps) {
  const panelId = useId()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [visible, setVisible] = useState(false)
  const [box, setBox] = useState<FloatingBox | null>(null)

  const parts = splitValue(value)
  const [date, setDate] = useState(parts.date)
  const [time, setTime] = useState(parts.time ?? '09:00')
  const selected = date ? parseDate(date) : null
  const [viewMonth, setViewMonth] = useState(() => selected ?? new Date())

  useEffect(() => {
    const next = splitValue(value)
    setDate(next.date)
    if (next.time) setTime(next.time)
    if (next.date) {
      const d = parseDate(next.date)
      if (d) setViewMonth(new Date(d.getFullYear(), d.getMonth(), 1))
    }
  }, [value])

  const updateBox = () => {
    const el = triggerRef.current
    if (!el) return
    setBox(computeDropdownBox(el.getBoundingClientRect(), 4, 288))
  }

  useEffect(() => {
    if (open) {
      setMounted(true)
      updateBox()
      const frame = requestAnimationFrame(() => setVisible(true))
      return () => cancelAnimationFrame(frame)
    }
    setVisible(false)
    const timer = window.setTimeout(() => setMounted(false), 200)
    return () => window.clearTimeout(timer)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onWin = () => updateBox()
    window.addEventListener('resize', onWin)
    window.addEventListener('scroll', onWin, true)
    return () => {
      window.removeEventListener('resize', onWin)
      window.removeEventListener('scroll', onWin, true)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onPointer = (event: MouseEvent) => {
      const t = event.target as Node
      if (triggerRef.current?.contains(t)) return
      if (panelRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <>
      <button
        id={id}
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={panelId}
        className={cn(
          'inline-flex h-9 w-full items-center justify-between gap-2 rounded-lg border-2 bg-raised px-3 text-left text-sm transition-opacity duration-200 disabled:cursor-not-allowed disabled:opacity-50',
          invalid ? 'border-danger' : 'border-border',
          className,
        )}
        onClick={() => setOpen((v) => !v)}
      >
        <span
          className={cn(
            'min-w-0 flex-1 truncate',
            value ? 'text-foreground' : 'text-muted',
          )}
        >
          {value || placeholder}
        </span>
        <CalendarClock className="size-4 shrink-0 text-muted" aria-hidden />
      </button>

      {mounted && box && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={panelRef}
              id={panelId}
              role="dialog"
              className={cn(
                'fixed z-50 overflow-hidden rounded-lg border border-border bg-raised transition-opacity duration-200',
                visible ? 'opacity-100' : 'opacity-0',
              )}
              style={floatingPanelStyle(box)}
            >
              <Calendar
                viewMonth={viewMonth}
                onViewMonthChange={setViewMonth}
                selected={selected}
                onSelectDay={(day) => setDate(formatDate(day))}
              />
              <div className="space-y-2 border-t border-border p-3">
                <TimePicker
                  value={time}
                  minuteStep={minuteStep}
                  onChange={setTime}
                />
                <Button
                  className="w-full"
                  disabled={!date}
                  onClick={() => {
                    if (!date) return
                    onChange?.(`${date} ${time}`)
                    setOpen(false)
                  }}
                >
                  确定
                </Button>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
